/**
 * ReasonRequiredModal (S2.2) — net-new shared component (holistic plan §Net-new).
 *
 * The ONE modal for every "this action needs a logged business reason" flow:
 * SO manual price override (first consumer, S2.2), PO >200k release (S3.1),
 * OCC exception snooze (S4.3), special-price saves (S9.1). Every submitted
 * reason is persisted by the calling mutation and surfaces in the audit lens.
 *
 * Namespace-agnostic by design: ALL user-facing strings arrive via props so
 * each consumer localizes from its own i18n namespace (the component itself
 * adds no keys and never falls back to hardcoded copy). Callers may render
 * extra context or inputs through `children` (e.g. the S2.2 override price
 * field) — the modal only owns the mandatory reason.
 */

import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Input, Modal, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';

const { Text } = Typography;

export interface ReasonRequiredModalProps {
  open: boolean;
  /** Localized modal title. */
  title: string;
  /** Localized primary button text. */
  submitText: string;
  /** Localized cancel button text. */
  cancelText: string;
  /** Localized label above the reason field (rendered with a red asterisk). */
  reasonLabel: string;
  /** Localized validation message when the reason is empty. */
  requiredMessage: string;
  /** Localized placeholder for the reason field. */
  reasonPlaceholder?: string;
  /** Optional localized helper paragraph above the content. */
  description?: string;
  /**
   * Optional caller-owned context/inputs rendered between the description and
   * the reason field (e.g. the price override InputNumber).
   */
  children?: ReactNode;
  width?: number;
  /**
   * Called with the trimmed reason. Return `false` to keep the modal open
   * (caller-side validation of `children` inputs failed); anything else lets
   * the caller close via its own `onCancel`/state.
   */
  onSubmit: (reason: string) => void | boolean | Promise<void | boolean>;
  onCancel: () => void;
}

interface ReasonFormData {
  reason: string;
}

const ReasonRequiredModal = ({
  open,
  title,
  submitText,
  cancelText,
  reasonLabel,
  requiredMessage,
  reasonPlaceholder,
  description,
  children,
  width = 520,
  onSubmit,
  onCancel,
}: ReasonRequiredModalProps) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();

  const schema = useMemo(
    () => z.object({ reason: z.string().trim().min(1, requiredMessage) }),
    [requiredMessage]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReasonFormData>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset({ reason: '' });
  }, [open, reset]);

  const submit = async (data: ReasonFormData) => {
    await onSubmit(data.reason.trim());
  };

  return (
    <Modal
      open={open}
      title={title}
      okText={submitText}
      cancelText={cancelText}
      onCancel={onCancel}
      onOk={handleSubmit(submit)}
      confirmLoading={isSubmitting}
      width={width}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      // A stray mask click must never discard a half-written audit reason.
      maskClosable={false}
      destroyOnHidden
    >
      {description && (
        <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
          {description}
        </Text>
      )}

      {children}

      <Text style={formStyles.labelStyle}>
        <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
        {reasonLabel}
      </Text>
      <Controller
        name="reason"
        control={control}
        render={({ field }) => (
          <Input.TextArea
            {...field}
            rows={3}
            placeholder={reasonPlaceholder}
            style={{ ...formStyles.inputStyle, height: 'auto' }}
            status={errors.reason ? 'error' : undefined}
          />
        )}
      />
      {errors.reason && <Text style={formStyles.errorStyle}>{errors.reason.message ?? ''}</Text>}
    </Modal>
  );
};

export default ReasonRequiredModal;
